import { useCallback, useMemo, useReducer } from 'react';
import { useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';

import { useAuth } from '#apps/client-web/features/auth/hooks/use-auth';
import { useLoadAuditByIdQuery } from '#apps/client-web/features/audit/hooks/use-load-audit-by-id-query';
import { type AuditService } from '#apps/client-web/features/audit/services/audit';
import { AUDITS_QUERY_KEY } from '#apps/client-web/features/audit/types/react-query';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { type FileStorageService } from '#apps/client-web/features/file-storage/services/file-storage';
import {
  useUploadAuditMutation,
  type AuditUploadState,
  type FormUploadState,
} from '#apps/client-web/features/upload/hooks/use-upload-audit-mutation';
import { type UploadApiService } from '#apps/client-web/features/upload/services/upload-api';
import { routes } from '#apps/client-web/app/routes';

import { type Audit, type AuditId } from '#packages/data-collection/models/audit';
import { type AuditBackgroundForm } from '#packages/data-collection/models/audit-background-form';
import { type AuditMechanicalForm } from '#packages/data-collection/models/audit-mechanical-form';
import { type AuditLightingTypeForm } from '#packages/data-collection/models/audit-lighting-type-form';
import { type AuditLightingCountForm } from '#packages/data-collection/models/audit-lighting-count-form';
import { type AuditWaterFixtureForm } from '#packages/data-collection/models/audit-water-fixture-form';
import { type AuditWindowsForm } from '#packages/data-collection/models/audit-windows-form';
import { type AuditChecklistForm } from '#packages/data-collection/models/audit-checklist-form';
import { type AuditEcmsForm } from '#packages/data-collection/models/audit-ecms-form';
import { reviewBackgroundForm } from '#packages/data-collection/models/audit-form-review/background';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { useDialog } from '#packages/web/hooks/use-dialog';

import { type FormReviewEntry } from './audit-review-submit-page.renderer';

export type AuditReviewSubmitPageViewModel = ReturnType<
  typeof useAuditReviewSubmitPageViewModel
>;

type AuditReviewSubmitPageViewModelProps = LoggingContext & {
  auditService: AuditService;
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  uploadApiService: UploadApiService;
  queryClient: QueryClient;
};

export type UpdateUploadStateAction =
  | {
      type: 'update';
      payload: Partial<Exclude<AuditUploadState, 'forms' | 'attachments'>>;
    }
  | {
      type: 'update_forms';
      payload: FormUploadState;
    }
  | {
      type: 'update_attachments';
      payload: Partial<AuditUploadState['attachments']>;
    }
  | {
      type: 'reset';
    };

const initialUploadState = Object.freeze({
  preparing: 'idle',
  forms: 'idle',
  attachments: {
    background: 'idle',
    mechanical: 'idle',
    'lighting-type': 'idle',
    'water-fixture': 'idle',
    windows: 'idle',
  },
} satisfies AuditUploadState);

const uploadStateReducer = (
  state: AuditUploadState,
  action: UpdateUploadStateAction,
): AuditUploadState => {
  switch (action.type) {
    case 'update_forms': {
      return { ...state, forms: action.payload };
    }
    case 'update_attachments': {
      return {
        ...state,
        attachments: { ...state.attachments, ...action.payload },
      };
    }
    case 'update': {
      return { ...state, ...action.payload };
    }
    case 'reset': {
      return { ...initialUploadState };
    }
  }
};

export const useAuditReviewSubmitPageViewModel = ({
  auditService,
  auditFormService,
  fileStorageService,
  uploadApiService,
  queryClient,
  logger,
}: AuditReviewSubmitPageViewModelProps) => {
  const { auditId } = useParams<{ auditId: AuditId }>();
  const { user } = useAuth({ logger });

  const auditQuery = useLoadAuditByIdQuery({
    auditService,
    queryClient,
    collectionName: 'active',
    auditId: auditId!,
  });

  const backgroundFormQuery = useLoadAuditFormByType<AuditBackgroundForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'background',
  });

  const mechanicalFormQuery = useLoadAuditFormByType<AuditMechanicalForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'mechanical',
  });

  const lightingTypeFormQuery = useLoadAuditFormByType<AuditLightingTypeForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'lighting-type',
  });

  const lightingCountFormQuery = useLoadAuditFormByType<AuditLightingCountForm>(
    {
      auditFormService,
      queryClient,
      auditId: auditId!,
      formType: 'lighting-count',
    },
  );

  const waterFixtureFormQuery = useLoadAuditFormByType<AuditWaterFixtureForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'water-fixture',
  });

  const windowsFormQuery = useLoadAuditFormByType<AuditWindowsForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'windows',
  });

  const checklistFormQuery = useLoadAuditFormByType<AuditChecklistForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'checklist',
  });

  const ecmsFormQuery = useLoadAuditFormByType<AuditEcmsForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'ecms',
  });

  const allQueries = [
    auditQuery,
    backgroundFormQuery,
    mechanicalFormQuery,
    lightingTypeFormQuery,
    lightingCountFormQuery,
    waterFixtureFormQuery,
    windowsFormQuery,
    checklistFormQuery,
    ecmsFormQuery,
  ];

  const formEntries = useMemo<FormReviewEntry[]>(
    () => [
      {
        formType: 'background',
        label: 'Background',
        link: routes.audit.getEnergyAuditBackgroundFormPath(auditId!),
        issues: reviewBackgroundForm(backgroundFormQuery.data ?? null),
      },
      {
        formType: 'mechanical',
        label: 'Mechanical',
        link: routes.audit.getEnergyAuditMechanicalFormPath(auditId!),
        issues: [],
      },
      {
        formType: 'lighting-type',
        label: 'Lighting Types',
        link: routes.audit.getEnergyAuditLightingTypeFormPath(auditId!),
        issues: [],
      },
      {
        formType: 'lighting-count',
        label: 'Lighting Count',
        link: routes.audit.getEnergyAuditLightingCountFormPath(auditId!),
        issues: [],
      },
      {
        formType: 'water-fixture',
        label: 'Water Fixtures',
        link: routes.audit.getEnergyAuditWaterFixtureFormPath(auditId!),
        issues: [],
      },
      {
        formType: 'windows',
        label: 'Windows',
        link: routes.audit.getEnergyAuditWindowsFormPath(auditId!),
        issues: [],
      },
      {
        formType: 'checklist',
        label: 'Checklist',
        link: routes.audit.getEnergyAuditChecklistFormPath(auditId!),
        issues: [],
      },
      {
        formType: 'ecms',
        label: 'ECMs',
        link: routes.audit.getEnergyAuditEcmsFormPath(auditId!),
        issues: [],
      },
    ],
    [auditId, backgroundFormQuery.data],
  );

  const pageState = useMemo<QueryPageState<{ audit: Audit; formEntries: FormReviewEntry[] }>>(() => {
    if (allQueries.every((q) => q.isSuccess)) {
      return {
        state: 'success',
        data: { audit: auditQuery.data!, formEntries },
      };
    }

    if (allQueries.some((q) => q.isLoading || q.isPending)) {
      return { state: 'loading' };
    }

    if (allQueries.some((q) => q.isError)) {
      const failedQuery = allQueries.find((q) => q.isError);
      return {
        state: 'error',
        error: failedQuery?.error ?? new Error('Failed to load form data'),
      };
    }

    throw new Error('Unhandled query state');
  }, [
    auditQuery.isSuccess,
    auditQuery.isLoading,
    auditQuery.isPending,
    auditQuery.isError,
    auditQuery.data,
    auditQuery.error,
    backgroundFormQuery.isSuccess,
    backgroundFormQuery.isLoading,
    backgroundFormQuery.isPending,
    backgroundFormQuery.isError,
    backgroundFormQuery.error,
    mechanicalFormQuery.isSuccess,
    mechanicalFormQuery.isLoading,
    mechanicalFormQuery.isPending,
    mechanicalFormQuery.isError,
    mechanicalFormQuery.error,
    lightingTypeFormQuery.isSuccess,
    lightingTypeFormQuery.isLoading,
    lightingTypeFormQuery.isPending,
    lightingTypeFormQuery.isError,
    lightingTypeFormQuery.error,
    lightingCountFormQuery.isSuccess,
    lightingCountFormQuery.isLoading,
    lightingCountFormQuery.isPending,
    lightingCountFormQuery.isError,
    lightingCountFormQuery.error,
    waterFixtureFormQuery.isSuccess,
    waterFixtureFormQuery.isLoading,
    waterFixtureFormQuery.isPending,
    waterFixtureFormQuery.isError,
    waterFixtureFormQuery.error,
    windowsFormQuery.isSuccess,
    windowsFormQuery.isLoading,
    windowsFormQuery.isPending,
    windowsFormQuery.isError,
    windowsFormQuery.error,
    checklistFormQuery.isSuccess,
    checklistFormQuery.isLoading,
    checklistFormQuery.isPending,
    checklistFormQuery.isError,
    checklistFormQuery.error,
    ecmsFormQuery.isSuccess,
    ecmsFormQuery.isLoading,
    ecmsFormQuery.isPending,
    ecmsFormQuery.isError,
    ecmsFormQuery.error,
    formEntries,
  ]);

  const [uploadState, dispatchUploadState] = useReducer(uploadStateReducer, {
    ...initialUploadState,
  });

  const resetUploadState = useCallback(
    () => dispatchUploadState({ type: 'reset' }),
    [],
  );

  const uploadAuditMutation = useUploadAuditMutation({
    auditId: auditId!,
    queryClient,
    auditService,
    auditFormService,
    fileStorageService,
    uploadApiService,
    getAccessToken: () => user?.access_token,
    logger,
    updateState: dispatchUploadState,
  });

  const uploadMutationState = useMemo<MutationPageState>(() => {
    if (uploadAuditMutation.isIdle) {
      return { state: 'idle' };
    }

    if (uploadAuditMutation.isSuccess) {
      return { state: 'success', data: null };
    }

    if (uploadAuditMutation.isPending) {
      return { state: 'pending' };
    }

    if (uploadAuditMutation.isError) {
      return { state: 'error', error: uploadAuditMutation.error };
    }

    throw new Error('Unhandled upload mutation state');
  }, [
    uploadAuditMutation.isIdle,
    uploadAuditMutation.isPending,
    uploadAuditMutation.isSuccess,
    uploadAuditMutation.isError,
    uploadAuditMutation.error,
  ]);

  const uploadDialog = useDialog<Audit>();

  const onUpload = useCallback(() => {
    if (pageState.state !== 'success') {
      logger?.debug('Cannot upload audit, page is not in success state');
      return;
    }

    uploadDialog.open(pageState.data.audit);
  }, [pageState, uploadDialog.open]);

  const onUploadConfirm = useCallback(() => {
    if (pageState.state !== 'success') {
      logger?.debug('Cannot upload, page is not in success state');
      return;
    }

    logger?.debug({ auditId }, 'Starting audit upload');
    uploadAuditMutation.mutate(pageState.data.audit, {
      onSuccess: async (result) => {
        logger?.debug(
          { auditId, serverAuditId: result.id },
          'Audit upload completed',
        );
        await queryClient.invalidateQueries({ queryKey: [AUDITS_QUERY_KEY] });
      },
      onError: (err) => {
        logger?.error({ err, auditId }, 'Audit upload failed');
      },
    });
  }, [pageState, uploadAuditMutation.mutate, auditId]);

  return {
    auditId,
    page: pageState,
    uploadState,
    uploadDialog,
    uploadMutationState,
    isUploadSuccess: uploadAuditMutation.isSuccess,
    resetUploadState,
    onUpload,
    onUploadConfirm,
  };
};
